import React, { useState, useEffect } from "react";
import {
  Users,
  BookOpen,
  Award,
  TrendingUp,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  BarChart3,
  PieChart,
  Loader,
  RefreshCw,
  ArrowUp,
  Eye,
  Target,
  Zap,
} from "lucide-react";
import { apiService } from "../../services/api";
import "./AdminDashboard.css";

const AdminDashboard = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState("last_30_days");
  const [topAssessments, setTopAssessments] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [adminStats, setAdminStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalAssessments: 0,
    completedAssessments: 0,
    averageScore: 0,
    completionRate: 0,
  });
  const [loading, setLoading] = useState(true);

  // Fetch admin data
  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        setLoading(true);

        const token = localStorage.getItem("authToken");
        if (!token) {
          window.location.href = "/login";
          return;
        }

        // Fetch all admin data in parallel
        const [
          assessmentsData,
          activityData,
          statsData,
          overviewData,
          growthData,
        ] = await Promise.allSettled([
          apiService.assessments.getTopAssessments(2),
          apiService.admin.getRecentActivity(10),
          apiService.admin.getStats(),
          apiService.admin.getAnalyticsOverview(selectedTimeframe),
          apiService.admin.getUserGrowthData(selectedTimeframe),
        ]);

        // Handle top assessments
        if (
          assessmentsData.status === "fulfilled" &&
          assessmentsData.value &&
          Array.isArray(assessmentsData.value)
        ) {
          setTopAssessments(assessmentsData.value);
        }

        // Handle recent activity
        if (
          activityData.status === "fulfilled" &&
          activityData.value?.success
        ) {
          const activities = activityData.value.activities.map((activity) => ({
            ...activity,
            icon: getIconComponent(activity.icon),
          }));
          setRecentActivity(activities);
        } else {
          setRecentActivity([
            {
              id: 1,
              type: "system_status",
              message: "System running smoothly",
              timestamp: "Now",
              icon: CheckCircle,
              color: "#10b981",
            },
          ]);
        }

        // Handle admin stats from overview
        if (
          overviewData.status === "fulfilled" &&
          overviewData.value?.success
        ) {
          setAdminStats((prev) => ({
            ...prev,
            ...overviewData.value.data,
          }));
        } else if (
          statsData.status === "fulfilled" &&
          statsData.value?.success
        ) {
          setAdminStats((prev) => ({
            ...prev,
            ...statsData.value.stats,
          }));
        }

        // Handle user growth data
        if (growthData.status === "fulfilled" && growthData.value?.success) {
          setUserGrowthData(growthData.value.data.slice(-6)); // Last 6 data points
        }
      } catch (err) {
        console.error("Error fetching admin data:", err);
        if (err.message && err.message.includes("401")) {
          localStorage.removeItem("authToken");
          localStorage.removeItem("currentUser");
          window.location.href = "/login";
          return;
        }
        // Error logged to console
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [selectedTimeframe]);

  const getIconComponent = (iconName) => {
    const iconMap = {
      Users,
      Award,
      BookOpen,
      AlertCircle,
      CheckCircle,
      Activity,
      TrendingUp,
    };
    return iconMap[iconName] || Activity;
  };

  const handleRefresh = () => {
    setLoading(true);
    setSelectedTimeframe((prev) => prev);
  };

  const engagementRate =
    adminStats.totalUsers > 0
      ? ((adminStats.activeUsers / adminStats.totalUsers) * 100).toFixed(1)
      : 0;

  if (loading) {
    return (
      <div className="admin-dashboard loading">
        <div className="loading-container">
          <Loader size={48} className="spinner" />
          <h2>Loading Dashboard...</h2>
          <p>Fetching latest platform data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-dashboard-header">
        <div className="header-content">
          <h1>Admin Dashboard</h1>
          <p>Monitor and manage your learning platform</p>
        </div>
        <div className="header-actions">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="timeframe-select"
          >
            <option value="last_7_days">Last 7 Days</option>
            <option value="last_30_days">Last 30 Days</option>
            <option value="last_90_days">Last 90 Days</option>
            <option value="last_year">Last Year</option>
          </select>
          <button className="btn btn-secondary" onClick={handleRefresh}>
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card blue">
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {adminStats.totalUsers.toLocaleString()}
            </div>
            <div className="stat-label">Total Users</div>
            <div className="stat-change positive">
              <ArrowUp size={14} />
              {engagementRate}% Active
            </div>
          </div>
        </div>

        <div className="admin-stat-card green">
          <div className="stat-icon">
            <Activity size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {adminStats.activeUsers.toLocaleString()}
            </div>
            <div className="stat-label">Active Users</div>
            <div className="stat-change positive">
              <ArrowUp size={14} />
              In selected period
            </div>
          </div>
        </div>

        <div className="admin-stat-card purple">
          <div className="stat-icon">
            <BookOpen size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{adminStats.totalAssessments}</div>
            <div className="stat-label">Total Assessments</div>
            <div className="stat-change positive">
              <Eye size={14} />
              Available tests
            </div>
          </div>
        </div>

        <div className="admin-stat-card orange">
          <div className="stat-icon">
            <Award size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {adminStats.completedAssessments.toLocaleString()}
            </div>
            <div className="stat-label">Completions</div>
            <div className="stat-change positive">
              <ArrowUp size={14} />
              Assessments done
            </div>
          </div>
        </div>

        <div className="admin-stat-card success">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{adminStats.averageScore}%</div>
            <div className="stat-label">Avg Score</div>
            <div className="stat-change positive">
              <Target size={14} />
              Overall performance
            </div>
          </div>
        </div>

        <div className="admin-stat-card primary">
          <div className="stat-icon">
            <Zap size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{adminStats.completionRate}%</div>
            <div className="stat-label">Completion Rate</div>
            <div className="stat-change positive">
              <CheckCircle size={14} />
              Success rate
            </div>
          </div>
        </div>
      </div>

      <div className="admin-dashboard-content">
        {/* User Growth Chart */}
        {userGrowthData.length > 0 && (
          <div className="admin-section user-growth">
            <div className="section-header">
              <h2>
                <BarChart3 size={20} />
                User Growth Trends
              </h2>
              <div className="chart-legend-inline">
                <span className="legend-item">
                  <span className="legend-dot blue"></span>
                  Total
                </span>
                <span className="legend-item">
                  <span className="legend-dot green"></span>
                  Active
                </span>
              </div>
            </div>
            <div className="growth-chart">
              <div className="chart-container">
                {userGrowthData.map((data, index) => {
                  const maxValue = Math.max(
                    ...userGrowthData.map((d) => d.totalUsers),
                  );
                  const totalHeight = (data.totalUsers / maxValue) * 100;
                  const activeHeight = (data.activeUsers / maxValue) * 100;

                  return (
                    <div key={index} className="chart-bar-group">
                      <div className="chart-bars">
                        <div
                          className="chart-bar total"
                          style={{ height: `${totalHeight}%` }}
                          title={`Total: ${data.totalUsers}`}
                        >
                          <span className="bar-value">{data.totalUsers}</span>
                        </div>
                        <div
                          className="chart-bar active"
                          style={{ height: `${activeHeight}%` }}
                          title={`Active: ${data.activeUsers}`}
                        >
                          <span className="bar-value">{data.activeUsers}</span>
                        </div>
                      </div>
                      <div className="chart-label">
                        {new Date(data.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="admin-section recent-activity">
          <div className="section-header">
            <h2>
              <Clock size={20} />
              Recent Activity
            </h2>
            <button
              className="view-all-btn"
              onClick={() => (window.location.href = "/admin/activity")}
            >
              <Eye size={16} />
              View All
            </button>
          </div>
          <div className="activity-list">
            {recentActivity.map((activity) => {
              const IconComponent = activity.icon;
              return (
                <div key={activity.id} className="activity-item">
                  <div
                    className="activity-icon"
                    style={{
                      backgroundColor: `${activity.color}20`,
                      color: activity.color,
                    }}
                  >
                    <IconComponent size={16} />
                  </div>
                  <div className="activity-content">
                    <p>{activity.message}</p>
                    <span className="activity-time">{activity.timestamp}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Assessments */}
        <div className="admin-section top-assessments">
          <div className="section-header">
            <h2>
              <Award size={20} />
              Top Assessments
            </h2>
            <button
              className="view-all-btn"
              onClick={() => (window.location.href = "/admin/top-assessments")}
            >
              <Eye size={16} />
              View All
            </button>
          </div>

          {topAssessments.length === 0 ? (
            <div className="no-data-container">
              <BookOpen size={48} />
              <h3>No Assessment Data</h3>
              <p>No assessments have been completed yet.</p>
            </div>
          ) : (
            <div className="assessments-list">
              {topAssessments.map((assessment, index) => (
                <div key={assessment.id || index} className="assessment-card">
                  <div className="assessment-rank">{index + 1}</div>
                  <div className="assessment-content">
                    <div className="assessment-header">
                      <h3 className="assessment-title">{assessment.name}</h3>
                      {assessment.skillName && (
                        <span className="skill-tag">
                          {assessment.skillName}
                        </span>
                      )}
                    </div>
                    <div className="assessment-metrics">
                      <div className="metric-item">
                        <span className="metric-label">Completions</span>
                        <span className="metric-value">
                          {assessment.completions.toLocaleString()}
                        </span>
                      </div>
                      <div className="metric-item">
                        <span className="metric-label">Avg Score</span>
                        <span className="metric-value score">
                          {assessment.avgScore}%
                        </span>
                      </div>
                    </div>
                    <div className="score-progress-bar">
                      <div
                        className="score-progress-fill"
                        style={{ width: `${assessment.avgScore}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="admin-section quick-actions">
          <div className="section-header">
            <h2>Quick Actions</h2>
          </div>
          <div className="actions-grid">
            <button
              className="action-card"
              onClick={() => (window.location.href = "/admin/users")}
            >
              <div className="action-icon blue">
                <Users size={24} />
              </div>
              <span>Manage Users</span>
            </button>
            <button
              className="action-card"
              onClick={() => (window.location.href = "/admin/assessments")}
            >
              <div className="action-icon purple">
                <BookOpen size={24} />
              </div>
              <span>Assessments</span>
            </button>
            <button
              className="action-card"
              onClick={() => (window.location.href = "/admin/analytics")}
            >
              <div className="action-icon orange">
                <PieChart size={24} />
              </div>
              <span>View Analytics</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
